'use client';

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { AlertCircle, Calculator, FileText } from 'lucide-react';

interface TaxResultCardProps {
  data: any;
  toolName: string;
}

const COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6'];

export default function TaxResultCard({ data, toolName }: TaxResultCardProps) {
  // Parsing robustly in case it's a string
  let parsedData = data;
  if (typeof data === 'string') {
    try {
      parsedData = JSON.parse(data);
    } catch (e) {
      return <div className="text-red-500">JSON Parsing Error</div>;
    }
  }

  // Handle enhanced and basic tax results
  const isEnhanced = toolName === 'calculate_enhanced_corporate_tax';
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 }).format(amount);
  };

  let chartData = [];
  const summary = {
    gross: 0,
    taxable: 0,
    totalTax: 0,
    effectiveRate: 0,
    takeHome: 0,
    breakdown: [] as any[]
  };

  if (isEnhanced && parsedData["基本情報"]) {
    summary.gross = parsedData["基本情報"]["当期純利益"] || 0;
    summary.taxable = parsedData["基本情報"]["課税所得"] || 0;
    summary.totalTax = parsedData["総合計"]["納付すべき税額合計"] || 0;
    summary.effectiveRate = parsedData["総合計"]["実効税率"] || 0;
    summary.takeHome = Math.max(0, summary.gross - summary.totalTax);

    chartData = [
      { name: '残る利益 (手取り)', value: summary.takeHome },
      { name: '国税 (法人税等)', value: parsedData["国税"]["国税合計"] || 0 },
      { name: '地方税合計', value: parsedData["地方税"]["地方税合計"] || 0 }
    ];

    summary.breakdown = [
      { name: '法人税', value: parsedData["国税"]["法人税"] },
      { name: '地方法人税', value: parsedData["国税"]["地方法人税"] },
      { name: '法人事業税', value: parsedData["地方税"]["事業税"] },
      { name: '特別法人事業税', value: parsedData["地方税"]["特別法人事業税"] },
      { name: '住民税(均等割)', value: parsedData["地方税"]["住民税均等割"] },
      { name: '住民税(法人税割)', value: parsedData["地方税"]["住民税法人税割"] },
    ];
  } else if (parsedData.gross_income !== undefined) {
    summary.gross = parsedData.gross_income;
    summary.taxable = parsedData.taxable_income;
    summary.totalTax = parsedData.total_tax;
    summary.effectiveRate = parsedData.effective_rate;
    summary.takeHome = Math.max(0, summary.gross - summary.totalTax);

    chartData = [
      { name: '残る利益 (手取り)', value: summary.takeHome },
      { name: '法人税', value: parsedData.corporate_tax },
      { name: '地方法人税', value: parsedData.local_corporate_tax },
      { name: '事業税', value: parsedData.business_tax },
      { name: '住民税', value: parsedData.resident_tax }
    ];

    summary.breakdown = [
      { name: '法人税', value: parsedData.corporate_tax },
      { name: '地方法人税', value: parsedData.local_corporate_tax },
      { name: '事業税', value: parsedData.business_tax },
      { name: '住民税合計', value: parsedData.resident_tax },
    ];
  } else {
    return (
      <div className="bg-gray-50 rounded-xl p-4 overflow-auto border border-gray-200">
        <pre className="text-xs text-gray-700 font-mono">{JSON.stringify(parsedData, null, 2)}</pre>
      </div>
    );
  }

  const assumptions = parsedData.assumptions || [];

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden my-4 w-full font-sans transition-all hover:shadow-xl">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-2 text-white">
          <Calculator className="h-6 w-6" />
          <h3 className="text-xl font-bold tracking-tight">税額シミュレーション結果</h3>
        </div>
        <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm font-medium">
          実効税率: {summary.effectiveRate}%
        </div>
      </div>

      <div className="p-6">
        {/* Main metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <p className="text-sm text-blue-600 font-medium mb-1">事業利益 (年間所得)</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.gross)}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4 border border-red-100">
            <p className="text-sm text-red-600 font-medium mb-1">納付税額合計</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalTax)}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
            <p className="text-sm text-emerald-600 font-medium mb-1">手取り利益 (税引後)</p>
            <p className="text-2xl font-bold text-emerald-700">{formatCurrency(summary.takeHome)}</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6 mb-8">
          {/* Chart */}
          <div className="w-full md:w-1/2 h-64 flex flex-col items-center justify-center bg-gray-50 rounded-xl border border-gray-100 p-2">
            <h4 className="text-sm font-bold text-gray-600 mb-2">利益と税金の割合</h4>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData.filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: any) => formatCurrency(Number(value))}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Breakdown Table */}
          <div className="w-full md:w-1/2">
            <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
              <FileText className="w-4 h-4 mr-1 text-gray-500" /> 税目別内訳
            </h4>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2">税目</th>
                    <th className="px-4 py-2 text-right">金額</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.breakdown.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2.5 text-gray-700">{item.name}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-gray-900">{formatCurrency(item.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Assumptions / Alerts */}
        {assumptions.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h4 className="text-sm font-bold text-amber-800 flex items-center mb-2">
              <AlertCircle className="w-4 h-4 mr-1" /> 計算の前提条件・免責事項
            </h4>
            <ul className="list-disc list-inside text-sm text-amber-700 space-y-1 ml-1">
              {assumptions.map((asm: string, idx: number) => (
                <li key={idx}>{asm}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
